import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useLazyGetOnboardingStatusQuery } from '../api/indexerApi';
import { IndexerShellPreload } from '../components/preloads/IndexerPreloads';
import { useProject } from './project-context';
import { useIndexerPaths } from './indexer-paths';

interface OnboardingGateValue {
  needsOnboarding: boolean;
  markCompleted: () => void;
  refreshStatus: () => Promise<void>;
}

const OnboardingGateContext = createContext<OnboardingGateValue | null>(null);

export function OnboardingGuard({ children }: { children: ReactNode }) {
  const { project, projectRef, loading: projectsLoading } = useProject();
  const location = useLocation();
  const paths = useIndexerPaths();
  const [checking, setChecking] = useState(false);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [getOnboardingStatus] = useLazyGetOnboardingStatusQuery();

  const onOnboarding = location.pathname.includes('/indexer/onboarding/');

  const refreshStatus = useCallback(async () => {
    // تا لود پروژه تمام نشده، نبودِ project یعنی «هنوز نمی‌دانیم» نه «پروژه نداریم»
    if (projectsLoading) return;
    if (!project || !projectRef) {
      // بدون پروژه هم باید به راه‌اندازی برود تا با «شروع راه‌اندازی» ساخته شود
      setNeedsOnboarding(true);
      return;
    }
    const knownDone = Boolean(project.onboardingCompletedAt);
    if (knownDone) {
      setNeedsOnboarding(false);
    } else {
      setChecking(true);
    }
    try {
      const status = await getOnboardingStatus(projectRef, true).unwrap();
      setNeedsOnboarding(!status.completed);
    } catch {
      setNeedsOnboarding(!project.onboardingCompletedAt);
    } finally {
      setChecking(false);
    }
  }, [projectsLoading, project, projectRef, getOnboardingStatus]);

  useEffect(() => {
    void refreshStatus();
  }, [refreshStatus, projectRef]);

  const markCompleted = useCallback(() => {
    setNeedsOnboarding(false);
  }, []);

  const gateValue = useMemo(
    () => ({ needsOnboarding, markCompleted, refreshStatus }),
    [needsOnboarding, markCompleted, refreshStatus],
  );

  // فقط وقتی وضعیت راه‌اندازی هنوز مشخص نیست UI را نگه می‌داریم؛
  // اگر پروژه قبلاً completed باشد، صفحه بلافاصله با preload خودش باز می‌شود.
  if (
    projectsLoading ||
    (project && checking && !project.onboardingCompletedAt)
  ) {
    return <IndexerShellPreload pathname={location.pathname} />;
  }

  if (needsOnboarding && !onOnboarding) {
    return (
      <OnboardingGateContext.Provider value={gateValue}>
        <Navigate to={paths.onboarding} replace />
      </OnboardingGateContext.Provider>
    );
  }

  if (project && !needsOnboarding && onOnboarding) {
    return (
      <OnboardingGateContext.Provider value={gateValue}>
        <Navigate to={paths.overview} replace />
      </OnboardingGateContext.Provider>
    );
  }

  return (
    <OnboardingGateContext.Provider value={gateValue}>
      {children}
    </OnboardingGateContext.Provider>
  );
}

export function useOnboardingGate() {
  const ctx = useContext(OnboardingGateContext);
  if (!ctx) {
    throw new Error('useOnboardingGate must be used inside OnboardingGuard');
  }
  return ctx;
}

export function useOnboardingNavHidden(): boolean {
  const location = useLocation();
  return location.pathname.includes('/indexer/onboarding/');
}

